import { r as registerInstance, c as createEvent, h, g as getElement } from './index-d4808923.js';
import { b as bem } from './index.esm-7182addc.js';
import { g as gravityXSSWhitelist, l as lib, x as xss } from './gravity-xss-whitelist-53c2079f.js';

const gravityToastCss = ".gravity-toast-stack{align-items:center;bottom:32px;display:flex;flex-direction:column;left:50%;margin-right:-50%;max-width:640px;padding:8px 16px;position:fixed;transform:translateX(-50%);z-index:4000}@keyframes countdown{0%{width:100%}100%{width:0}}.gravity-toast{animation:fadeIn 150ms cubic-bezier(0.32, 0, 0.32, 1);border-radius:6px;box-shadow:0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431), inset 0 0 0 1px rgba(0, 15, 30, 0.05);margin:0 8px 8px;overflow:hidden;position:relative}.gravity-toast__content{align-items:center;background-color:#FFFFFF;display:flex;gap:12px;padding:0 16px}.gravity-toast__text{display:block;padding:16px 0}.gravity-toast__icon{display:inline-flex;vertical-align:bottom}.gravity-toast__bar-track{background-color:rgba(0, 15, 30, 0.05);height:4px;width:100%}.gravity-toast:hover .gravity-toast__bar--animate{animation-play-state:paused}.gravity-toast__bar{height:100%;width:100%}.gravity-toast__bar--animate{animation:countdown linear var(--toast-countdown-runtime) forwards}.gravity-toast__bar--style-info{background-color:rgba(0, 15, 30, 0.2)}.gravity-toast__bar--style-warning{background-color:#FF9000}.gravity-toast__bar--style-error{background-color:#DB0A40}.gravity-toast__bar--style-success{background-color:#159D48}.gravity-toast--hidden{opacity:0}.gravity-toast__action{align-items:center;cursor:pointer;display:flex;gap:12px}.gravity-toast__action-text{display:block;text-decoration:underline}@keyframes fadeIn{0%{opacity:0;transform:translateY(calc(100% + 52px))}100%{opacity:1;transform:translateY(0)}}";
const GravityToastStyle0 = gravityToastCss;

const GravityToast = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.cancel = createEvent(this, "cancel", 7);
        this.details = createEvent(this, "details", 7);
        this.dismiss = createEvent(this, "dismiss", 7);
        this.generateClassNames = bem('gravity-toast');
        this.xssOptions = {
            whiteList: Object.assign(Object.assign({}, gravityXSSWhitelist), lib.exports.whiteList),
        };
        this.messages = [];
    }
    /**
     * Enqueues a new message with the given text and options.
     *
     * @param {string} messageText - The text of the message to enqueue.
     * @param {MessageOptions} [options={}] - Optional settings for the message.
     * @returns {Promise<string>} - A promise that resolves to a string representing the message ID.
     */
    async enqueueMessage(messageText, options = {}) {
        var _a;
        const id = 'k' + (new Date().getTime() + Math.random()).toString().replace('.', '');
        const actions = (_a = options.actions) !== null && _a !== void 0 ? _a : [];
        if (!options.actions) {
            if (options.onDetails || options.details) {
                actions.push({
                    label: 'Details',
                    onClick: () => {
                        this.handleDetails(id, options);
                    },
                });
            }
            if ((options.variant == 'process' && options.onCancel) || options.cancellable) {
                actions.push({
                    label: 'Cancel',
                    onClick: () => {
                        this.handleCancel(id, options);
                    },
                });
            }
        }
        const message = Object.assign(Object.assign({ id,
            messageText, style: (options === null || options === void 0 ? void 0 : options.style) ? options.style : 'info', variant: (options === null || options === void 0 ? void 0 : options.variant) ? options.variant : 'temporary' }, (options.icon ? { icon: options.icon } : {})), { cancellable: options.cancellable, details: options.details, actions });
        if (message.variant === 'temporary') {
            this.startCountdown(message);
        }
        this.messages = [...this.messages, message];
        return message.id;
    }
    /**
     * Removes an existing message by its ID.
     *
     * @param {string} id - The ID of the message to remove.
     * @returns {Promise<void>} - A promise that resolves when the message is removed.
     */
    async removeMessage(id) {
        const messageDiv = this.element.shadowRoot.getElementById(id);
        if (messageDiv !== null) {
            messageDiv.style.opacity = '0';
        }
        this.messages = this.messages.filter((message) => message.id !== id);
    }
    /**
     * Updates an existing message with the given text.
     *
     * @param {string} id - The ID of the message to update.
     * @param {string} messageText - The new text of the message.
     * @returns {Promise<void>} - A promise that resolves when the message is updated.
     */
    async updateMessage(id, messageText) {
        this.messages = this.messages.map((message) => {
            if (message.id === id) {
                message = Object.assign(Object.assign({}, message), { messageText });
            }
            return message;
        });
    }
    getIconForMessage(message) {
        if (message.variant == 'process') {
            const loaderVariant = message.style == 'error' || message.style === 'success' ? 'light' : 'primary';
            return h("gravity-loading", { class: this.generateClassNames('icon'), variant: loaderVariant });
        }
        else if (message.icon) {
            return (h("gravity-icon", { color: this.getIconColorBasedOnStyle(message.style), size: "x-small", name: message.icon, class: this.generateClassNames('icon') }));
        }
        return '';
    }
    renderActions(message) {
        if (!message.actions) {
            return;
        }
        return message.actions.map((action) => (h("a", { class: this.generateClassNames('action'), onClick: () => {
                if (action.onClick) {
                    action.onClick();
                }
            } }, h("gravity-text", { class: this.generateClassNames('action-text'), size: "xx-small", color: "dark" }, action.label))));
    }
    handleDismiss(message) {
        const id = message.id;
        this.dismiss.emit(id);
        this.removeMessage(id);
    }
    handleCancel(id, options) {
        this.cancel.emit(id);
        if (options.onCancel) {
            options.onCancel();
        }
    }
    handleDetails(id, options) {
        this.details.emit(id);
        if (options.onDetails) {
            options.onDetails();
        }
    }
    calcRuntimeForMessage(message) {
        // Remove HTML tags from message text
        const plainText = message.messageText.replace(/<\/?[^>]+(>|$)/g, '');
        const runtime = Math.max(4000, plainText.split(' ').length * 400);
        return runtime;
    }
    startCountdown(message, duration) {
        const runtime = this.calcRuntimeForMessage(message);
        message.countdown = {
            timeout: setTimeout(() => {
                this.removeMessage(message.id);
            }, duration || runtime),
            started: Date.now(),
            remaining: duration || runtime,
            runtime,
        };
    }
    pauseCountdown(message) {
        var _a;
        if (message === null || message === void 0 ? void 0 : message.countdown) {
            clearTimeout((_a = message.countdown) === null || _a === void 0 ? void 0 : _a.timeout);
            message.countdown.remaining = message.countdown.remaining - (Date.now() - message.countdown.started);
        }
    }
    resumeCountdown(message) {
        if (message === null || message === void 0 ? void 0 : message.countdown) {
            this.startCountdown(message, message.countdown.remaining);
        }
    }
    handleMouseEnter(message) {
        if (message.variant == 'temporary') {
            this.pauseCountdown(message);
        }
    }
    handleMouseLeave(message) {
        if (message.variant == 'temporary') {
            this.resumeCountdown(message);
        }
    }
    getIconColorBasedOnStyle(style) {
        switch (style) {
            case 'error':
                return 'negative';
            case 'success':
                return 'positive';
            case 'info':
                return 'dark';
            case 'warning':
                return 'informative';
            default:
                return 'dark';
        }
    }
    render() {
        return (h("div", { key: '62ee608a4c8159b3f48e3495363fd455b59743e9', class: "gravity-toast-stack" }, this.messages.map((message) => {
            var _a;
            return (h("div", { class: this.generateClassNames({ style: message.style }), id: message.id, key: message.id, onMouseEnter: () => this.handleMouseEnter(message), onMouseLeave: () => this.handleMouseLeave(message) }, h("style", null, `#${message.id}{
                  --toast-countdown-runtime: ${(_a = message.countdown) === null || _a === void 0 ? void 0 : _a.runtime}ms;
              }`), h("div", { class: this.generateClassNames('bar-track') }, h("div", { class: this.generateClassNames('bar', {
                    style: message.style,
                    animate: message.variant === 'temporary',
                }) })), h("div", { class: this.generateClassNames('content') }, this.getIconForMessage(message) ? this.getIconForMessage(message) : null, h("gravity-text", { class: this.generateClassNames('text'), size: "x-small", color: "dark", weight: "regular", innerHTML: xss(message.messageText, this.xssOptions) }), this.renderActions(message), message.variant == 'persistent' ? (h("gravity-button", { "data-testid": "dismiss", class: this.generateClassNames('action'), onClick: () => this.handleDismiss(message), type: "secondary", size: "small", iconName: "close" })) : null)));
        })));
    }
    get element() { return getElement(this); }
};
GravityToast.style = GravityToastStyle0;

export { GravityToast as gravity_toast };

//# sourceMappingURL=gravity-toast.entry.js.map